import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { useHistory } from "react-router";
import { useLocation } from "react-router-dom";
import queryString from "querystring";
import { Form, FormRenderProps, useFormState } from "react-final-form";
import makeStyles from "@material-ui/core/styles/makeStyles";
import Typography from "@material-ui/core/Typography";
import { Field } from "react-final-form";
import { useQuery } from "react-query";
import Autocomplete from "../../../../components/Autocomplete";
import { useCardsPage } from "../../cardsPageContext";
import usePrivateClassCardsQueries from "../../hooks/usePrivateClassCardsQueries";
import { getName } from "country-list";

const useStyles = makeStyles(() => ({
  root: {
    width: 391,
    padding: "24px 16px",
    borderRadius: 15,
    backgroundColor: "#ffffff",
    boxShadow: "0 0 8px #C2C9D682",
    top: 119,
    position: "sticky",
  },
  container: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#231F20",
    lineHeight: "29px",
  },
  subTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#231F20",
    lineHeight: "17px",
    marginTop: 11,
  },
  formControl: {
    marginTop: 21,
  },
}));

const FilterForm: FunctionComponent<FormRenderProps> = ({ submitError }) => {
  const classes = useStyles();
  const { values } = useFormState();
  const location = useLocation();
  const { handleFilterChange } = useCardsPage();
  const history = useHistory();

  const { fetchCountries, fetchSubjects, fetchTeachingLanguages } =
    usePrivateClassCardsQueries();

  const { isLoading, isFetching, data } = useQuery<any>(
    "privateClassCountries",
    fetchCountries,
  );

  const {
    isLoading: isLoadingSubjects,
    isFetching: isFetchingSubjects,
    data: subjectsData,
  } = useQuery<any>("privateClassSubjects", fetchSubjects);

  const {
    isLoading: isLoadingLanguages,
    isFetching: isFetchingLanguages,
    data: languagesData,
  } = useQuery<any>("privateClassTeachingLanguages", fetchTeachingLanguages);

  useEffect(() => {
    const urlOptions: Record<string, any> = {};
    if (values.countries?.length) {
      urlOptions.countries = values.countries
        .map((country) => country.code)
        ?.join();
    }
    if (values.subjects?.length) {
      urlOptions.subjects = values.subjects
        .map((subject) => subject.id)
        ?.join();
    }
    if (values.teachingLanguages?.length) {
      urlOptions.teachingLanguages = values.teachingLanguages
        .map((lang) => lang.code)
        ?.join();
    }
    history.replace({
      pathname: location.pathname,
      search: "?filters=" + JSON.stringify(urlOptions),
    });
    handleFilterChange?.(values);
  }, [values]);

  return (
    <form>
      {submitError && <Typography color="error">{submitError}</Typography>}
      <div className={classes.formControl}>
        <Field
          fullWidth
          multiple={true}
          name="subjects"
          label="Subject"
          placeholder="Subject"
          options={subjectsData?.data}
          loading={(isFetchingSubjects || isLoadingSubjects).toString()}
          disabled={isLoadingSubjects || isFetchingSubjects}
          disableCloseOnSelect
          component={Autocomplete}
        />
      </div>
      <div className={classes.formControl}>
        <Field
          fullWidth
          multiple={true}
          name="teachingLanguages"
          label="Teaching Language"
          placeholder="Teaching Language"
          options={languagesData?.data}
          loading={(isFetchingLanguages || isLoadingLanguages).toString()}
          disabled={isLoadingLanguages || isFetchingLanguages}
          disableCloseOnSelect
          component={Autocomplete}
        />
      </div>
      <div className={classes.formControl}>
        <Field
          fullWidth
          multiple={true}
          name="countries"
          label="Country"
          placeholder="Country"
          options={data?.data}
          loading={(isFetching || isLoading).toString()}
          disabled={isLoading || isFetching}
          disableCloseOnSelect
          component={Autocomplete}
        />
      </div>
    </form>
  );
};

const Filter = () => {
  const classes = useStyles();

  const location = useLocation();
  const { handleFilterChange } = useCardsPage();
  const history = useHistory();

  let initialValues = useMemo(() => {
    let queryParams = queryString.parse(
      location.search.split("?").join(""),
      " ",
    );

    if (typeof queryParams.filters === "string") {
      let filters = JSON.parse(queryParams.filters);

      if (Object.keys(filters).length > 0) {
        const filterForm: Record<string, any> = {};
        if (filters.countries) {
          const codes = filters.countries.split(",");
          filterForm.countries = codes.map((code) => ({
            code,
            name: getName(code),
          }));
        }
        if (filters.subjects) {
          const ids = filters.subjects.split(",");
          filterForm.subjects = ids.map((id) => ({
            id: Number(id),
            name: id,
          }));
        }
        if (filters.teachingLanguages) {
          const codes = filters.teachingLanguages.split(",");
          filterForm.teachingLanguages = codes.map((code) => ({
            code,
            name: code,
          }));
        }
        return filterForm;
      }
    }

    return {};
  }, []);

  const submitHandler = useCallback(async (data?) => {
    const urlOptions: Record<string, any> = {};
    if (data?.countries?.length) {
      urlOptions.countries = data.countries
        .map((country) => country.code)
        ?.join();
    }
    if (data?.subjects?.length) {
      urlOptions.subjects = data.subjects.map((subject) => subject.id)?.join();
    }
    if (data?.teachingLanguages?.length) {
      urlOptions.teachingLanguages = data.teachingLanguages
        .map((lang) => lang.code)
        ?.join();
    }
    history.replace({
      pathname: location.pathname,
      search: "?filters=" + JSON.stringify(urlOptions),
    });
    handleFilterChange?.(data);
  }, []);

  useEffect(() => {
    submitHandler(initialValues);
  }, []);

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        <Typography className={classes.title} component={"h1"}>
          Filter
        </Typography>
        <Typography className={classes.subTitle} component={"h2"}>
          Search for Private Classes
        </Typography>
      </div>
      <Form
        initialValues={initialValues}
        onSubmit={submitHandler as any}
        keepDirtyOnReinitialize
        component={FilterForm}
      />
    </div>
  );
};

export default React.memo(Filter);
